import React, { useEffect, useMemo } from 'react';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import NewBilling from 'features/settings/components/NewBilling';
import UserSettingsGeneralPanel from 'features/settings/components/UserSettingsGeneralPanel';
import ProjectSettingsPanel from 'features/settings/components/ProjectSettingsPanel';
import ModelPricesPanel from 'features/settings/components/panels/ModelPricesPanel';
import ModelDetailPanel from 'features/settings/components/panels/ModelDetailPanel';
import EventsPanel from 'features/settings/components/panels/EventsPanel';
import CohortsPanel from 'features/settings/components/panels/CohortsPanel';
import ModelsPanel from 'features/settings/components/panels/ModelsPanel';
import AskSetupPanel from 'features/settings/components/panels/AskSetupPanel';
import McpPanel from 'features/settings/components/panels/McpPanel';
import DatasetsExportPanel from 'features/settings/components/panels/DatasetsExportPanel';
import IntegrationsPanel from 'features/settings/components/panels/IntegrationsPanel';
import OrgSlackPanel from 'features/settings/components/panels/OrgSlackPanel';
import ConnectedAccountsPanel from 'features/settings/components/panels/ConnectedAccountsPanel';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  resolveSettingsSection,
  SETTINGS_SECTION_HEADING,
} from 'features/settings/settingsNavConfig';

/**
 * Settings is the workspace's single configuration surface. The rail
 * (Sidebar.js) groups sections into Project / Organization / Personal;
 * this page just resolves the URL to a panel.
 */

/**
 * Connectors that get their own page under Settings > Integrations, keyed by
 * URL segment. Only GitHub needs one — connecting is just step one there, and
 * repo + agent mapping can't collapse into a row like the others do.
 */
const CONNECTOR_HEADING = { github: 'GitHub' };

/** Sections whose panel wants the full width (tables, card grids). */
const WIDE_SECTIONS = new Set([
  'members', 'billing', 'model-prices',
  'events', 'cohorts', 'models', 'mcp', 'integrations', 'datasets',
]);

const Settings = () => {
  const { team } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const basePath = `/${team}/settings`;

  const section = useMemo(() => resolveSettingsSection(location.pathname), [location.pathname]);

  // /:team/settings/model-prices/:model → drill-down detail.
  const model = useMemo(() => {
    const segs = (location.pathname || '').split('/').filter(Boolean);
    return segs[2] === 'model-prices' && segs[3] ? decodeURIComponent(segs[3]) : null;
  }, [location.pathname]);

  // /:team/settings/integrations/:connector → that connector's own page.
  // Only GitHub has one; the rest resolve inline or in a modal.
  const connector = useMemo(() => {
    const segs = (location.pathname || '').split('/').filter(Boolean);
    return segs[2] === 'integrations' && segs[3] ? segs[3] : null;
  }, [location.pathname]);

  /**
   * Top-bar path names the current page, matching the rest of the app —
   * Settings / Model prices / gpt-4o. Panels no longer carry their own
   * heading + description block.
   */
  const crumbs = useMemo(() => {
    // GlobalTopBar renders every non-last segment as <Link to={seg.to || '#'}>,
    // so a segment without `to` becomes a link to the current page — it looks
    // clickable and does nothing. Give the root a real target; /settings
    // redirects to Project > General below.
    const out = [{ label: 'Settings', to: basePath }];
    if (model) {
      out.push(
        { label: SETTINGS_SECTION_HEADING['model-prices'], to: `${basePath}/model-prices` },
        { label: model },
      );
    } else if (connector) {
      out.push(
        { label: SETTINGS_SECTION_HEADING.integrations, to: `${basePath}/integrations` },
        { label: CONNECTOR_HEADING[connector] || connector },
      );
    } else if (SETTINGS_SECTION_HEADING[section]) {
      out.push({ label: SETTINGS_SECTION_HEADING[section] });
    }
    return out;
  }, [model, connector, section, basePath]);
  useBreadcrumb(crumbs);

  // Default /settings → Project > General.
  useEffect(() => {
    if (team && (location.pathname === basePath || location.pathname === `${basePath}/`)) {
      navigate(`${basePath}/project`, { replace: true });
    }
  }, [team, basePath, location.pathname, navigate]);

  // Canonicalise legacy segments so the URL matches what's rendered.
  // resolveSettingsSection() already maps these, but leaving the old URL in the
  // bar means a stale link keeps propagating.
  useEffect(() => {
    if (!team) return;
    const seg = location.pathname.split('/').filter(Boolean)[2];
    const CANONICAL = {
      general: 'profile', // personal settings moved to Profile
      notifications: 'profile', // notification prefs live on Profile
      config: 'usage',
      teamsSection: 'members',
    };
    // Issue thresholds are alerts now (Issues v6); an old link goes there.
    if (seg === 'issue-thresholds' || seg === 'issues') {
      navigate(`/${team}/alerts`, { replace: true });
      return;
    }
    const target = CANONICAL[seg];
    if (target) navigate(`${basePath}/${target}`, { replace: true });
  }, [team, basePath, location.pathname, navigate]);

  return (
    <div
      className="flex min-h-screen w-full flex-1 flex-col bg-surface"
      style={{ fontFamily: 'Inter, sans-serif', backgroundColor: 'var(--surface-base)' }}
    >
      <main className="min-w-0 flex-1 overflow-y-auto bg-surface px-4 pb-12 pt-6 md:px-8 lg:px-12">
        <div
          className={
            WIDE_SECTIONS.has(section)
              ? 'w-full max-w-none text-left'
              : 'mx-auto w-full max-w-[640px] text-left'
          }
        >
          {section === 'profile' ? (
            <UserSettingsGeneralPanel />
          ) : section === 'connected-accounts' ? (
            <ConnectedAccountsPanel />
          ) : section === 'project' ? (
            <ProjectSettingsPanel />
          ) : section === 'events' ? (
            <EventsPanel />
          ) : section === 'cohorts' ? (
            <CohortsPanel />
          ) : section === 'integrations' ? (
            <IntegrationsPanel connector={connector} />
          ) : section === 'org-slack' ? (
            <OrgSlackPanel />
          ) : section === 'mcp' ? (
            <McpPanel />
          ) : section === 'datasets' ? (
            <DatasetsExportPanel />
          ) : section === 'models' ? (
            <ModelsPanel />
          ) : section === 'ask' ? (
            <AskSetupPanel />
          ) : section === 'model-prices' ? (
            model ? <ModelDetailPanel model={model} /> : <ModelPricesPanel />
          ) : (
            <NewBilling variant="embedded" />
          )}
        </div>
      </main>
    </div>
  );
};

export default Settings;
